import React, { useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { TbArrowBackUp, TbDeviceFloppy } from "react-icons/tb";
import { CircularProgress } from "@mui/material";
import { useThemeManager } from "../hooks/useThemeManager";
import { useCss } from "../context/CssContext";
import ThemeSection from "./ThemeSection";
import SuccessAlert from "./alerts/SuccessAlert";
import ErrorAlert from "./alerts/ErrorAlert";
import config from "../config";
import "./CssPanel.css";

const CssPanelNew = ({ open, onClose }) => {
  const { themeValues, themeConfig, flatConfig, updateThemeValue, resetToDefaults, syncFromApi } =
    useThemeManager();
  const { fetchAndApplyCss } = useCss();
  
  const [loading, setLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [panelLoading, setPanelLoading] = useState(false);

  // Alert mesajlarını otomatik temizle
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => setErrorMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Panel açıldığında API'den güncel CSS değerlerini yükle
  useEffect(() => {
    if (open) {
      loadCurrentCssValues();
    }
  }, [open]);

  // API'den güncel CSS değerlerini al ve theme manager'ı güncelle
  const loadCurrentCssValues = async () => {
    setPanelLoading(true);
    
    try {
      console.log('🔄 CSS paneli için güncel değerler yükleniyor...');
      
      // Güvenlik kontrolleri
      if (!window.rh || !window.rh.cr) {
        console.warn('⚠️ Güvenlik sistemi yüklenmemiş');
        setPanelLoading(false);
        return;
      }

      const payload = JSON.stringify({
        process_id: "get-css",
        user_id: "",
      });

      const keys = await window.rh.cr(payload);
      
      const response = await fetch(
        `${config.apiBaseUrl}`,
        {
          method: "POST",
          headers: { 
            "Content-Type": "application/json",
            "hk": keys[0],
            "hh": keys[1]
          },
          body: payload,
        }
      );

      if (!response.ok) {
        console.warn('⚠️ CSS API isteği başarısız');
        setPanelLoading(false);
        return;
      }

      const result = await response.json();
      
      // API response formatına göre CSS verilerini al
      let cssData = result;
      
      // Eğer result.status varsa ve başarılıysa data field'ını kullan
      if (result.status && result.data) {
        cssData = result.data;
      } else if (result.status === false) {
        console.warn('⚠️ CSS API hatası:', result.message || 'Bilinmeyen hata');
        setPanelLoading(false);
        return;
      }
      
      // CSS verilerini kontrol et ve theme manager'ı güncelle
      if (cssData && typeof cssData === 'object' && Object.keys(cssData).length > 0) {
        console.log('✅ CSS panel için güncel değerler yüklendi:', Object.keys(cssData).length, 'özellik');
        syncFromApi(cssData);
      } else {
        console.log('📝 CSS verisi bulunamadı, default değerler kullanılıyor');
        syncFromApi(null);
      }

    } catch (error) {
      console.error('❌ CSS panel değerleri yükleme hatası:', error);
    } finally {
      setPanelLoading(false);
    }
  };

  if (!open) return null;

  // CSS verilerini API'ye kaydet
  const saveCssToApi = async () => {
    setLoading(true);
    
    try {
      const cssData = {};

      // Sadece değiştirilmiş CSS değerlerini ekle
      Object.keys(flatConfig).forEach((key) => {
        const config = flatConfig[key];
        if (config && config.cssVar) {
          cssData[config.cssVar] = themeValues[key];
        }
      });

      const response = await fetch(`${config.adminApi}/set_css`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({data:  cssData}),
        mode: 'cors',
        credentials: 'include',
      });

      const result = await response.json();

      if (response.ok && result.status) {
        setSuccessMessage(result.message || "CSS ayarları başarıyla kaydedildi!");
        setErrorMessage('');
        
        await fetchAndApplyCss();
      } else {
        setErrorMessage(result.message || 'CSS kaydetme hatası oluştu');
        setSuccessMessage('');
      }
    } catch (error) {
      console.error('CSS kaydetme hatası:', error);
      setErrorMessage("Bağlantı hatası! Lütfen tekrar deneyin.");
      setSuccessMessage('');
    } finally {
      setLoading(false);
      setShowSaveModal(false);
    }
  };

  // Varsayılan ayarlara sıfırla (onaylı)
  const handleConfirmedReset = () => {
    resetToDefaults();
    setShowResetModal(false);
    setSuccessMessage("Ayarlar varsayılan değerlere sıfırlandı!");
    setErrorMessage('');
  };

  // Bölüm başlıkları
  const sectionTitles = {
    general: "Genel Ayarlar",
    header: "Header Ayarları",
    mainCategory: "Ana Kategori Ayarları",
    tab: "Tab Ayarları",
    bonusButtons: "Bonus Kart Butonları",
    bonusConfirmPopup: "Bonus Onay Popup'ı",
    rulesPopup: "Kurallar Popup'ı",
    historyPopup: "Geçmiş Popup'ı",
    multipleBonusDialog: "Çoklu Bonus Dialog'u",
    bonusCard: "Bonus Kart'ı",
  };

  return (
    <div className="css-panel">
      {/* Alert Mesajları */}
      {successMessage && <SuccessAlert message={successMessage} />}
      {errorMessage && <ErrorAlert message={errorMessage} />}
      
      <div className="css-panel-header">
        <h2>CSS Düzenleme Paneli</h2>
        <div className="header-actions">
          <button
            className="save-button"
            onClick={() => setShowSaveModal(true)}
            disabled={loading}
            title="CSS ayarlarını kaydet"
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: '#22c55e' }} />
            ) : (
              <TbDeviceFloppy size={20} />
            )}
            CSS Kaydet
          </button>
          <button
            className="reset-button"
            onClick={() => setShowResetModal(true)}
            disabled={loading}
            title="Varsayılan ayarlara dön"
          >
            <TbArrowBackUp size={20} />
            Sıfırla
          </button>
          <button className="close-button" onClick={onClose}>
            <IoMdClose size={24} />
          </button>
        </div>
      </div>

      <div className="css-panel-content">
        {panelLoading ? (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            padding: '40px 20px',
            gap: '15px'
          }}>
            <CircularProgress 
              size={40} 
              thickness={4} 
              sx={{ color: '#1375E1' }} 
            />
            <p style={{ 
              color: '#666', 
              margin: 0, 
              fontSize: '14px',
              textAlign: 'center'
            }}>
              Güncel CSS değerleri yükleniyor...
            </p>
          </div>
        ) : (
          Object.entries(themeConfig).map(([sectionKey, properties]) => (
          <ThemeSection
            key={sectionKey}
            title={sectionTitles[sectionKey]}
            sectionKey={sectionKey}
            properties={properties}
            themeValues={themeValues}
            onThemeChange={updateThemeValue}
            defaultExpanded={sectionKey === "general"}
          />
          ))
        )}
      </div>

      {/* CSS Kaydet Onay Modal */}
      {showSaveModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>CSS Ayarlarını Kaydet</h3>
            </div>
            <div className="modal-content">
              <p>Mevcut CSS ayarlarını kaydetmek istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                Bu işlem sunucudaki CSS ayarlarını güncelleyecektir.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowSaveModal(false)}
                disabled={loading}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button"
                onClick={saveCssToApi}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                    Kaydediliyor...
                  </>
                ) : (
                  'Evet, Kaydet'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Varsayılana Sıfırla Onay Modal */}
      {showResetModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>Varsayılan Ayarlara Sıfırla</h3>
            </div>
            <div className="modal-content">
              <p>Tüm CSS ayarlarını varsayılan değerlere sıfırlamak istediğinizden emin misiniz?</p>
              <p style={{ fontSize: '14px', color: '#e74c3c', marginTop: '8px' }}>
                ⚠️ Bu işlem geri alınamaz! Mevcut ayarlarınız kaybolacaktır.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="modal-cancel-button"
                onClick={() => setShowResetModal(false)}
              >
                İptal
              </button>
              <button
                className="modal-confirm-button reset"
                onClick={handleConfirmedReset}
              >
                Evet, Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CssPanelNew;
